<template>
  <div class="rounded-md border border-gray-200 bg-gray-50 p-3">
    <div class="flex flex-col lg:flex-row lg:items-center gap-3">
      <div class="flex rounded-md border border-gray-300 overflow-hidden flex-shrink-0 self-start">
        <button v-for="t in TYPES" :key="t.value" type="button" class="px-3 py-2 text-sm whitespace-nowrap" :class="modelValue.type === t.value ? 'bg-primary-1 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'" @click="setType(t.value)">{{ t.label }}</button>
      </div>

      <div v-if="modelValue.type === 'year'" class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>ปี พ.ศ.</span>
        <select :value="modelValue.year_from" class="form-select w-28" aria-label="ปีที่เริ่มใช้" @change="setYearFrom(Number($event.target.value))">
          <option v-for="n in years" :key="n" :value="n">{{ n }}</option>
        </select>
        <span>ถึง</span>
        <select :value="modelValue.year_to || ''" class="form-select w-44" aria-label="ปีที่สิ้นสุด" @change="update({ year_to: $event.target.value ? Number($event.target.value) : null })">
          <option v-for="n in yearsTo" :key="n" :value="n">{{ n === Number(modelValue.year_from) ? `${n} (ปีเดียว)` : n }}</option>
          <option value="">ไม่มีกำหนดสิ้นสุด</option>
        </select>
      </div>

      <div v-else-if="modelValue.type === 'range'" class="flex flex-wrap items-center gap-1.5 text-xs text-theme-37">
        <span>เติมให้อัตโนมัติ:</span>
        <button v-for="fy in fiscalYears" :key="fy" type="button" class="chip chip-outline" style="cursor:pointer" @click="update(fiscalYearRange(fy))">ปีงบประมาณ {{ fy }}</button>
      </div>

      <div v-else class="text-sm text-theme-37">ใช้ได้ทุกปี ไม่มีวันหมดอายุ</div>
    </div>

    <!-- the two dates get a row of their own: three selects each do not fit beside the mode switch -->
    <div v-if="modelValue.type === 'range'" class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm text-gray-700">
      <div><div class="text-xs text-theme-37 mb-1">วันที่เริ่ม <span class="opacity-70">(เว้นว่าง = ไม่กำหนด)</span></div><ThaiDateInput :model-value="modelValue.start_date" label="วันที่เริ่ม" @update:modelValue="(v) => update({ start_date: v })" /></div>
      <div><div class="text-xs text-theme-37 mb-1">วันที่สิ้นสุด <span class="opacity-70">(เว้นว่าง = ไม่มีกำหนดสิ้นสุด)</span></div><ThaiDateInput :model-value="modelValue.end_date" label="วันที่สิ้นสุด" @update:modelValue="(v) => update({ end_date: v })" /></div>
    </div>

    <div v-if="modelValue.type !== 'always'" class="flex flex-wrap items-center gap-2 mt-2 text-xs">
      <span v-if="error" class="field-error mt-0">{{ error }}</span>
      <template v-else>
        <span class="chip" :style="PERIOD_STATUS_STYLE[status]">{{ PERIOD_STATUS_LABEL[status] }}</span>
        <span class="text-theme-37">{{ rangeText }} · นอกช่วงนี้ระบบจะไม่แนะนำสวัสดิการนี้บนหน้าครัวเรือน</span>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ThaiDateInput from "./ThaiDateInput.vue";
import { currentYearBE, fiscalYearRange, formatThaiDate, periodBounds, periodError, periodStatus, PERIOD_STATUS_LABEL, PERIOD_STATUS_STYLE } from "../period";

/** "which year is this welfare for": every year · a พ.ศ. year (default) · explicit start / end dates */
const props = defineProps({
  modelValue: { type: Object, required: true },   // { type, year_from, year_to, start_date, end_date }
});
const emit = defineEmits(["update:modelValue"]);

const TYPES = [
  { value: "year", label: "ระบุปี" },
  { value: "range", label: "ระบุวันที่เริ่ม–สิ้นสุด" },
  { value: "always", label: "ทุกปี" },
];
const now = currentYearBE();
const update = (patch) => emit("update:modelValue", { ...props.modelValue, ...patch });

const years = computed(() => {
  const list = [];
  for (let n = now - 5; n <= now + 10; n++) list.push(n);
  for (const v of [props.modelValue.year_from, props.modelValue.year_to]) if (v && !list.includes(Number(v))) list.push(Number(v));
  return list.sort((a, b) => a - b);
});
const yearsTo = computed(() => years.value.filter((n) => n >= Number(props.modelValue.year_from || 0)));
const fiscalYears = [now, now + 1];

function setType(type) {
  if (type === props.modelValue.type) return;
  if (type === "year") {
    // keep the year the admin was already looking at when coming from a date range
    const y = props.modelValue.start_date ? Number(props.modelValue.start_date.slice(0, 4)) + 543 : props.modelValue.year_from || now;
    update({ type, year_from: y, year_to: props.modelValue.year_to && props.modelValue.year_to >= y ? props.modelValue.year_to : y });
  } else if (type === "range") {
    const { start, end } = periodBounds(props.modelValue);
    update({ type, start_date: props.modelValue.start_date || start, end_date: props.modelValue.end_date || end });
  } else update({ type });
}
/** a single-year period follows the start year; a span is only pushed forward when it would end before it starts */
function setYearFrom(y) {
  const { year_from: prev, year_to: to } = props.modelValue;
  update({ year_from: y, year_to: to === null || to === undefined ? null : (Number(to) === Number(prev) || Number(to) < y ? y : to) });
}

const error = computed(() => periodError(props.modelValue));
const status = computed(() => periodStatus(props.modelValue));
const rangeText = computed(() => {
  const { start, end } = periodBounds(props.modelValue);
  if (start && end) return `ใช้ได้ ${formatThaiDate(start)} – ${formatThaiDate(end)}`;
  if (start) return `ใช้ได้ตั้งแต่ ${formatThaiDate(start)} เป็นต้นไป`;
  return end ? `ใช้ได้ถึง ${formatThaiDate(end)}` : "";
});
</script>
